import { apiRequest } from "./api";
import type { SuggestedBonus } from "./bonusConfigApi";

export type PayslipStatus = "DRAFT" | "FINALIZED";

export interface PayslipDeduction {
  label: string;
  amount: number;
}

export interface ProrationDetails {
  hireDate: string;
  effectiveStart: string;
  workedDays: number;
  totalDays: number;
}

export interface PpaAdjustment {
  adjId?: string;
  label: string;
  amount: number;
  type: "DEDUCTION" | "ADDITION";
}

export interface CashAdvanceDeductionEntry {
  label: string;
  amount: number;
  cashAdvId: string;
}

export interface Payslip {
  userId: string;
  period: string;
  /** Business that owns this payslip — present in multi-business responses */
  businessId?: string;
  basicPay: number;
  allowances: PayslipDeduction[];
  incentives?: PayslipDeduction[];
  deductions: PayslipDeduction[];
  loanDeductions: PayslipDeduction[];
  cashAdvanceDeductions?: CashAdvanceDeductionEntry[];
  workAdjustments?: PayslipDeduction[];
  ppaAdjustments?: PpaAdjustment[];
  employerContributions?: PayslipDeduction[];
  grossPay: number;
  netPay: number;
  status: PayslipStatus;
  isProrated?: boolean;
  prorationDetails?: ProrationDetails;
  createdAt: string;
  updatedAt: string;
  /** Denormalized employee name stored at processing time */
  employeeName?: string;
  processedBy?: string;
  processedAt?: string;
  finalizedBy?: string;
  finalizedAt?: string;
}

export interface PayslipProcessingData {
  payslip: Payslip | null;
  profile: {
    firstName: string;
    lastName: string;
    position: string;
    department: string;
    baseSalary: number;
  } | null;
  pendingInstallments: Array<{
    loanId: string;
    seq: number;
    amount: number;
    SK: string;
  }>;
  pendingAdjustments: Array<{
    adjId: string;
    label: string;
    amount: number;
    type: "DEDUCTION" | "ADDITION";
    reason?: string;
    sourceDate?: string;
  }>;
  pendingCashAdvances?: Array<{
    cashAdvId: string;
    label: string;
    amount: number;
    remainingAmount: number;
  }>;
  suggestedBonuses?: SuggestedBonus[];
}

export interface SavePayslipPayload {
  userId: string;
  businessId?: string;
  period: string;
  basicPay: number;
  allowances: PayslipDeduction[];
  incentives: PayslipDeduction[];
  deductions: PayslipDeduction[];
  loanDeductions: PayslipDeduction[];
  ppaAdjustments: PpaAdjustment[];
  cashAdvanceDeductions?: CashAdvanceDeductionEntry[];
  workAdjustments?: PayslipDeduction[];
}

export const payslipApi = {
  listMine: (params?: { limit?: number; cursor?: string }) =>
    apiRequest<{ items: Payslip[]; nextCursor: string | null }>("/me/payslips", {
      params: {
        limit: params?.limit ?? 10,
        ...(params?.cursor ? { cursor: params.cursor } : {}),
      },
    }),
  /** Fetch a specific payslip for the current employee, identified by businessId + period. */
  getMine: (businessId: string, period: string) =>
    apiRequest<{ payslip: Payslip }>(
      `/me/payslips/${encodeURIComponent(businessId)}/${encodeURIComponent(period)}`,
    ),
  downloadMine: (businessId: string, period: string) =>
    apiRequest<{ url: string }>(
      `/me/payslips/${encodeURIComponent(businessId)}/${encodeURIComponent(period)}/download`,
    ),

  // ── Super-admin routes ─────────────────────────────────────────────────────
  adminList: (period: string, limit = 50, lastKey?: string, businessId?: string) =>
    apiRequest<{ items: Payslip[]; nextKey: string | null }>("/admin/payslips", {
      params: { period, limit, ...(lastKey ? { lastKey } : {}), ...(businessId ? { businessId } : {}) },
    }),
  adminGet: (userId: string, period: string, businessId?: string) =>
    apiRequest<PayslipProcessingData>(
      `/admin/payslips/${encodeURIComponent(userId)}/${encodeURIComponent(period)}`,
      businessId ? { params: { businessId } } : undefined,
    ),
  adminSave: (payload: SavePayslipPayload) =>
    apiRequest<{ payslip: Payslip }>("/admin/payslips", { method: "POST", body: payload }),
  adminFinalize: (userId: string, period: string, businessId?: string) =>
    apiRequest<{ success: boolean }>(
      `/admin/payslips/${encodeURIComponent(userId)}/${encodeURIComponent(period)}/finalize`,
      { method: "POST", ...(businessId ? { params: { businessId } } : {}) },
    ),
  adminDownload: (userId: string, period: string, businessId?: string) =>
    apiRequest<{ url: string }>(
      `/admin/payslips/${encodeURIComponent(userId)}/${encodeURIComponent(period)}/download`,
      businessId ? { params: { businessId } } : undefined,
    ),

  // ── Business-scoped admin routes ───────────────────────────────────────────
  // List uses the payroll run route (business-scoped, same GSI query)
  bizAdminList: (businessId: string, period: string) =>
    apiRequest<{ items: Payslip[]; nextKey: string | null }>(
      `/biz/${encodeURIComponent(businessId)}/payroll/${encodeURIComponent(period)}/payslips`,
    ),
  // Get processing data (profile + installments) for overriding a payslip
  bizAdminGet: (businessId: string, userId: string, period: string) =>
    apiRequest<PayslipProcessingData>(
      `/biz/${encodeURIComponent(businessId)}/payslips/${encodeURIComponent(userId)}/${encodeURIComponent(period)}/processing`,
    ),
  bizAdminSave: (businessId: string, payload: SavePayslipPayload) =>
    apiRequest<{ payslip: Payslip }>(
      `/biz/${encodeURIComponent(businessId)}/payslips`,
      { method: "POST", body: payload },
    ),
  bizAdminFinalize: (businessId: string, userId: string, period: string) =>
    apiRequest<{ success: boolean }>(
      `/biz/${encodeURIComponent(businessId)}/payslips/${encodeURIComponent(userId)}/${encodeURIComponent(period)}/finalize`,
      { method: "POST" },
    ),
  bizAdminDownload: (businessId: string, userId: string, period: string) =>
    apiRequest<{ url: string }>(
      `/biz/${encodeURIComponent(businessId)}/payslips/${encodeURIComponent(userId)}/${encodeURIComponent(period)}/download`,
    ),
};
